import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { Avatar, Box } from "@mui/material";
import CustomSelect from "@/components/CustomSelect";
import Flex from "@/components/Flex";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  ATTENDANCE_STATUS_OPTIONS,
  ATTENDANCE_STATUS_PLACEHOLDER,
  PARTICIPANT_NOTE_STATE,
  PARTICIPANT_NOTE_STATE_LABEL,
  SESSION_NOTE_LABEL,
} from "../group-visit-constant";
import { AttendanceStatus, SessionParticipant } from "../group-visit-types";
import { getInitials, isNoteRequired } from "../group-visit-utils";

export type AttendanceRowProps = {
  participant: SessionParticipant;
  onStatusChange: (uuid: string, status: AttendanceStatus) => void;
  /** Opens the individual note for this participant; ignored while disabled. */
  onSelect: (uuid: string) => void;
  /** Opens this participant's signed visit note; omitted while it has none. */
  onShowSummary?: (uuid: string) => void;
  isSelected?: boolean;
  disabled?: boolean;
};

const AVATAR_SIZE = 22;
const PATIENT_ID_FONT_SIZE = "10px";
const STATUS_SELECT_MAX_WIDTH = 160;

/**
 * One participant in the attendance rail — presence and its note state.
 *
 * @author Rajkumar Rathod
 */
const AttendanceRow = ({
  participant,
  onStatusChange,
  onSelect,
  onShowSummary,
  isSelected,
  disabled,
}: AttendanceRowProps) => {
  const noteRequired = isNoteRequired(participant.status);
  const hasNote = participant.noteState !== PARTICIPANT_NOTE_STATE.NOT_STARTED;

  return (
    <Flex
      flexDirection="column"
      gap={0.5}
      sx={{
        py: 1,
        px: 1,
        borderRadius: CHART_DIMENSIONS.borderRadius,
        border: `1px solid ${CHART_COLORS.borderLight}`,
        ...(isSelected && {
          border: `1px solid ${CHART_COLORS.btnPrimaryBg}`,
          backgroundColor: CHART_COLORS.bgSecondaryBlue,
        }),
      }}
    >
      {/* A signed-off session has no note to open — only its summary. */}
      <Flex
        alignItems="center"
        gap={1}
        role={disabled ? undefined : "button"}
        tabIndex={disabled ? undefined : 0}
        onClick={disabled ? undefined : () => onSelect(participant.uuid)}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Enter" || event.key === " ") {
            onSelect(participant.uuid);
          }
        }}
        sx={{ cursor: disabled ? "default" : "pointer", minWidth: 0 }}
      >
        <Avatar
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            fontSize: "10px",
            bgcolor: CHART_COLORS.bgSecondaryBlue,
            color: CHART_COLORS.btnSecondaryText,
          }}
        >
          {getInitials(participant.name)}
        </Avatar>

        <Flex flexDirection="column" sx={{ minWidth: 0 }}>
          <Text
            noWrap
            {...CHART_TYPOGRAPHY.body5}
            fontWeight={CHART_TYPOGRAPHY.weight.medium}
            c={CHART_COLORS.textDarker}
          >
            {participant.name}
          </Text>
          {!!participant.patientId && (
            <Text
              size={PATIENT_ID_FONT_SIZE}
              c={CHART_COLORS.textMutedGrey}
              noWrap
            >
              {`${SESSION_NOTE_LABEL.patientId} - ${participant.patientId}`}
            </Text>
          )}
        </Flex>
      </Flex>

      <Flex alignItems="center" justifyContent="space-between" gap={1}>
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: STATUS_SELECT_MAX_WIDTH }}>
          <CustomSelect
            name={`attendance-${participant.uuid}`}
            options={ATTENDANCE_STATUS_OPTIONS}
            value={participant.status}
            placeholder={ATTENDANCE_STATUS_PLACEHOLDER}
            disabled={disabled}
            clearable={false}
            disableSortOptions
            onChange={(value) =>
              onStatusChange(participant.uuid, value as AttendanceStatus)
            }
          />
        </Box>

        {noteRequired ? (
          <StatusChip
            sx={{ flexShrink: 0 }}
            label={PARTICIPANT_NOTE_STATE_LABEL[participant.noteState]}
            variant="default"
          />
        ) : (
          !!participant.status && (
            <Text
              {...CHART_TYPOGRAPHY.body5}
              c={CHART_COLORS.textMutedGrey}
              noWrap
              sx={{ flexShrink: 0 }}
            >
              {SESSION_NOTE_LABEL.noNoteRequired}
            </Text>
          )
        )}
      </Flex>

      {!!onShowSummary && noteRequired && hasNote && (
        <Flex justifyContent="flex-end">
          <StyledButtonV2
            label={SESSION_NOTE_LABEL.showSummary}
            variant="text"
            sIcon={
              <PrintOutlinedIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />
            }
            onClick={() => onShowSummary(participant.uuid)}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default AttendanceRow;
